/**
 * Wizard-hook registrations entry point.
 *
 * Imported once at SPA boot from `main.tsx`. Each hook module exports
 * a `WizardHook<...>` constant; we register them here in one place.
 *
 * Adding a new hook: drop a file under `./hooks/<plugin>-<event>.tsx`
 * (e.g. `./hooks/backup-after-app.tsx`), export a `WizardHook`
 * constant, then add the import + register call here.
 *
 * Once dynamic plugin loading lands, plugins will register from their
 * own bundle's boot path — this file remains the "core" registry
 * entry for the first-party hooks shipped with the SPA.
 */
import { registerWizardHook } from './registry.js';
import { applicationAfterOrgHook } from './hooks/application-after-org.js';

let registered = false;

/**
 * Idempotent — safe to call multiple times (e.g. from a HMR-friendly
 * boot path that re-imports `main.tsx`).
 */
export function registerWizardHooks(): void {
  if (registered) return;
  registered = true;

  registerWizardHook(applicationAfterOrgHook);

  // Future hooks land here. Examples of what each plugin would do:
  //
  //   registerWizardHook(backupAfterAppHook);
  //     // eventKind: 'app.created', order: 20
  //     // After an app lands, ask the operator about backup policies
  //     // for any database/files Components on it.
  //
  //   registerWizardHook(monitoringAfterAppHook);
  //     // eventKind: 'app.created', order: 30
  //     // After backup, offer to attach a monitor to the web Component.
  //
  //   registerWizardHook(issueTrackerAfterAppHook);
  //     // eventKind: 'app.created', order: 40
}
